import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Flex,
  Modal,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import { useApiUrl, useCustomMutation } from "@refinedev/core";
import { IconBug, IconEdit, IconReload } from "@tabler/icons";
import { TeaAddonOperation } from "entities/tea_addon_renewal_run";
import {
  colorsByStatus,
  colorsByErrorCode,
  colorsByOperationType,
} from "entities/tea_addon_renewal_run/operationStatus";
import { useEffect, useState } from "react";
import { formatDateTime } from "utils/dates";
import { Link } from "react-router-dom";

type OperationResponse = {
  success: boolean;
  error?: string;
};

type TeaAddonOperationsProps = {
  tea_addon_operations: TeaAddonOperation[];
  onOperation: Function;
  showRenewalRun?: boolean;
};

export const TeaAddonOperations = ({
  tea_addon_operations,
  onOperation,
  showRenewalRun = false,
}: TeaAddonOperationsProps) => {
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isNoteOpen, setNoteOpen] = useState<boolean>(false);
  const [selectedOperation, setSelectedOperation] =
    useState<TeaAddonOperation | null>(null);
  const [operationNote, setOperationNote] = useState<string>("");
  const apiUrl = useApiUrl();

  const { mutate: retryOperation, isLoading: isRetrying } =
    useCustomMutation<OperationResponse>();

  const { mutate: updateOperationNote, isLoading: isUpdatingOperationNote } =
    useCustomMutation<OperationResponse>();

  useEffect(() => {
    setOperationNote(selectedOperation?.notes || "");
  }, [selectedOperation?.id, selectedOperation?.notes]);

  return (
    <>
      <Modal
        opened={isErrorOpen}
        onClose={() => setErrorOpen(false)}
        title="Error"
        size="80%"
      >
        <Text color="red">
          <pre>{errorMessage}</pre>
        </Text>
      </Modal>
      <Modal
        opened={isNoteOpen}
        onClose={() => {
          setNoteOpen(false);
          setSelectedOperation(null);
        }}
        size="lg"
        title="Notes"
      >
        <Textarea
          value={operationNote}
          onChange={(e) => setOperationNote(e.target.value)}
        />
        <Button
          mt="lg"
          disabled={isUpdatingOperationNote}
          onClick={() =>
            selectedOperation?.id &&
            updateOperationNote(
              {
                url: `${apiUrl}/tea_addon_operations/${selectedOperation.id}`,
                method: "patch",
                values: { content: operationNote },
              },
              {
                onSuccess: () => {
                  setNoteOpen(false);
                  setSelectedOperation(null);
                  onOperation();
                },
              }
            )
          }
        >
          Save
        </Button>
      </Modal>

      <Table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Operation Type</th>
            <th>Status / Error Code</th>
            <th>User</th>
            <th>Tea Subscription</th>
            {showRenewalRun && <th>Renewal Run</th>}
            <th>Tea Addon</th>
            <th>Runs Count</th>
            <th>Last Run At</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {tea_addon_operations.map((tea_addon_operation) => {
            const showActions =
              tea_addon_operation.status !== "succeeded" &&
              tea_addon_operation.error_message;

            return (
              <tr>
                <td>{tea_addon_operation.id}</td>
                <td>
                  <Badge
                    color={
                      colorsByOperationType[tea_addon_operation.operation_type]
                    }
                    variant="filled"
                    size="sm"
                  >
                    {tea_addon_operation.operation_type === "dunning"
                      ? "Dunning"
                      : "Renewal"}
                  </Badge>
                </td>
                <td>
                  <Flex direction="column" gap="xs">
                    <Badge color={colorsByStatus[tea_addon_operation.status]}>
                      {tea_addon_operation.status}
                    </Badge>
                    {tea_addon_operation.operation_type === "dunning" &&
                      tea_addon_operation.last_dunning_attempt && (
                        <Badge color="red" variant="dot" size="sm">
                          Last Attempt
                        </Badge>
                      )}
                    {tea_addon_operation.status === "failed" &&
                      tea_addon_operation.error_code && (
                        <Badge
                          color={
                            colorsByErrorCode[tea_addon_operation.error_code]
                          }
                          variant="outline"
                        >
                          {tea_addon_operation.error_code}
                        </Badge>
                      )}
                  </Flex>
                </td>
                <td>
                  <Anchor
                    component={Link}
                    to={`/users/show/${tea_addon_operation.user.id}`}
                  >
                    {tea_addon_operation.user?.email}
                  </Anchor>
                </td>
                <td>
                  <Anchor
                    component={Link}
                    to={`/tea_addon_subscriptions/show/${tea_addon_operation.tea_addon_subscription.id}`}
                  >
                    {tea_addon_operation.tea_addon_subscription?.id}
                  </Anchor>
                </td>
                {showRenewalRun && (
                  <td>
                    {tea_addon_operation.tea_addon_renewal_run_id ? (
                      <Anchor
                        component={Link}
                        to={`/tea_addon_renewal_runs/show/${tea_addon_operation.tea_addon_renewal_run_id}`}
                      >
                        #{tea_addon_operation.tea_addon_renewal_run_id}
                      </Anchor>
                    ) : (
                      <Badge color="gray">-</Badge>
                    )}
                  </td>
                )}
                <td>
                  {tea_addon_operation.tea_addon ? (
                    <Anchor
                      component={Link}
                      to={`/tea_addons/show/${tea_addon_operation.tea_addon.id}`}
                    >
                      {tea_addon_operation.tea_addon.name}
                    </Anchor>
                  ) : (
                    <Badge color="gray">-</Badge>
                  )}
                </td>
                <td>{tea_addon_operation.runs_count}</td>
                <td>
                  {(() => {
                    const formatted = formatDateTime(tea_addon_operation.last_run_at);
                    return formatted ? (
                      <Text size="sm">{formatted}</Text>
                    ) : (
                      <Badge color="gray" variant="light">—</Badge>
                    );
                  })()}
                </td>
                <td>
                  {(() => {
                    const formatted = formatDateTime(tea_addon_operation.created_at);
                    return formatted ? (
                      <Text size="sm">{formatted}</Text>
                    ) : (
                      <Badge color="gray" variant="light">—</Badge>
                    );
                  })()}
                </td>
                <td>
                  <Flex gap="xs">
                    <ActionIcon
                      variant={
                        tea_addon_operation.notes !== "" ? "filled" : "outline"
                      }
                      size="sm"
                      color={
                        tea_addon_operation.notes !== "" ? "green" : "gray"
                      }
                      onClick={() => {
                        setNoteOpen(true);
                        setSelectedOperation(tea_addon_operation);
                      }}
                    >
                      <IconEdit />
                    </ActionIcon>

                    {showActions ? (
                      <>
                        <ActionIcon
                          color="red"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setErrorOpen(true);
                            setErrorMessage(tea_addon_operation.error_message);
                          }}
                        >
                          <IconBug />
                        </ActionIcon>
                        <ActionIcon
                          color="blue"
                          variant="outline"
                          disabled={isRetrying}
                          size="sm"
                          onClick={() => {
                            retryOperation(
                              {
                                url: `${apiUrl}/tea_addon_operations/${tea_addon_operation.id}/retry`,
                                method: "post",
                                values: {},
                              },
                              {
                                onSuccess: () => onOperation(),
                                onError: () => onOperation(),
                              }
                            );
                          }}
                        >
                          <IconReload />
                        </ActionIcon>
                      </>
                    ) : (
                      <div />
                    )}
                  </Flex>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};
